import { Field, ErrorMessage, FormikProps } from 'formik';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { QuestionFormValues } from '@/utils/forms/questionTransformers';
import { isIndividualSport } from '@low6-pickem/sports-config';

interface QuestionBasicFieldsProps {
  formik: FormikProps<QuestionFormValues>;
  pickems: Array<{ pickem_id: string; title_en?: string; title_fr?: string }>;
  isEditable: boolean;
  isNewQuestion: boolean;
  sport?: string;
}

export function QuestionBasicFields({
  formik,
  pickems,
  isEditable,
  isNewQuestion: _isNewQuestion,
  sport,
}: QuestionBasicFieldsProps) {
  const { errors, touched, values, setFieldValue } = formik;

  const individualSport = sport ? isIndividualSport(sport) : false;

  // Check if question type is set by grading type (readonly mode)
  const gradingType = values.question_metadata?.grading_type;
  const isQuestionTypeLockedByGrading = Boolean(gradingType);

  return (
    <div className="flex flex-col gap-4">
      {/* Pick'em dropdown - full width */}
      <div className="flex flex-col gap-2">
        <Label htmlFor="pickem_id">Pickem</Label>
        <Select
          value={values.pickem_id}
          onValueChange={value => setFieldValue('pickem_id', value)}
          disabled={!isEditable}
        >
          <SelectTrigger
            data-testid="question-pickem_id-select"
            className={errors.pickem_id && touched.pickem_id ? 'border-red-500' : ''}
          >
            <SelectValue placeholder="Select a pickem" />
          </SelectTrigger>
          <SelectContent>
            {pickems.map(pickem => (
              <SelectItem key={pickem.pickem_id} value={pickem.pickem_id.toString()}>
                {pickem.title_en || pickem.title_fr || `Pickem #${pickem.pickem_id}`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <ErrorMessage name="pickem_id">
          {msg => <span className="text-sm text-red-500">{msg}</span>}
        </ErrorMessage>
      </div>

      {/* Question Type dropdown - full width */}
      <div className="flex flex-col gap-2">
        <Label htmlFor="question_type">Question Type</Label>
        <Select
          value={values.question_type}
          onValueChange={value => setFieldValue('question_type', value)}
          disabled={!isEditable || isQuestionTypeLockedByGrading || !_isNewQuestion}
        >
          <SelectTrigger
            data-testid="question-question_type-select"
            className={errors.question_type && touched.question_type ? 'border-red-500' : ''}
          >
            <SelectValue placeholder="Select type" />
          </SelectTrigger>
          <SelectContent>
            {!individualSport && <SelectItem value="team">Team Selection</SelectItem>}
            <SelectItem value="player">Player Selection</SelectItem>
            <SelectItem value="player_over_under">Player Over/Under</SelectItem>
            {!individualSport && <SelectItem value="team_over_under">Team Over/Under</SelectItem>}
            {/* DKPLAT-354 cross-fixture leader questions — span every fixture in the
                pickem and pick the candidate with the highest cumulative stat.
                Hidden for individual sports (e.g. golf): the feed has no leader
                stat support for PGA yet, so offering it was a dead end. */}
            {!individualSport && (
              <SelectItem value="player_leader">Player Leader (cross-fixture)</SelectItem>
            )}
            {!individualSport && (
              <SelectItem value="team_leader">Team Leader (cross-fixture)</SelectItem>
            )}
            <SelectItem value="tiebreaker">Tiebreaker</SelectItem>
            <SelectItem value="custom">Custom</SelectItem>
          </SelectContent>
        </Select>
        <ErrorMessage name="question_type">
          {msg => <span className="text-sm text-red-500">{msg}</span>}
        </ErrorMessage>
        {isQuestionTypeLockedByGrading && (
          <p className="text-xs text-muted-foreground">
            Question type is automatically set based on the grading configuration
          </p>
        )}
        {!_isNewQuestion && (
          <p className="text-xs text-muted-foreground">
            Question type cannot be changed after creation
          </p>
        )}
      </div>

      {/* Points input - full width */}
      {values.question_type !== 'tiebreaker' && (
        <div className="flex flex-col gap-2">
          <Label htmlFor="points">Points</Label>
          <Field
            as={Input}
            type="number"
            id="points"
            name="points"
            data-testid="question-points"
            placeholder="Points for correct answer"
            disabled={!isEditable}
            className={errors.points && touched.points ? 'border-red-500' : ''}
          />
          <ErrorMessage name="points">
            {msg => <span className="text-sm text-red-500">{msg}</span>}
          </ErrorMessage>
        </div>
      )}

      {/* Correct Value input - full width */}
      {values.question_type === 'tiebreaker' && (
        <div className="flex flex-col gap-2">
          <Label htmlFor="correct_value">Correct Value</Label>
          <Field
            as={Input}
            type="number"
            step="0.01"
            id="correct_value"
            name="correct_value"
            data-testid="question-correct_value"
            placeholder="Enter correct tiebreaker value"
            disabled={!isEditable}
            className={errors.correct_value && touched.correct_value ? 'border-red-500' : ''}
          />
          <ErrorMessage name="correct_value">
            {msg => <span className="text-sm text-red-500">{msg}</span>}
          </ErrorMessage>
        </div>
      )}

      {/* Daily Double Toggle */}
      {values.question_type !== 'tiebreaker' && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Switch
              data-testid="question-daily_double-switch"
              checked={values.question_metadata?.dailyDouble || false}
              onCheckedChange={checked =>
                setFieldValue('question_metadata', {
                  ...(values.question_metadata || {}),
                  dailyDouble: checked,
                })
              }
              disabled={!isEditable}
            />
            <Label>Daily Double</Label>
          </div>
          <p className="text-xs text-muted-foreground">
            Mark this question as a Daily Double for special styling
          </p>
        </div>
      )}
    </div>
  );
}
